import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import {
  Rocket, ShieldCheck, MessageCircle, Mic, FileText, Banknote, Share2,
  LifeBuoy, Sparkles, Building2, Users, Megaphone, Activity, BarChart3,
  Pill, Phone, X, ArrowRight, CircleDot, Check
} from "lucide-react";

/** Strictly Projexino palette */
const BRAND_BLUE = "#004a99";
const BRAND_ORANGE = "#f37021";
const BLUE_TINT = "#e6f0ff";
const ORANGE_TINT = "#fff1e8";

const ROADMAPS = {
  clinic_admin: {
    title: "Your launch roadmap",
    subtitle: "From sign-in to your first paying patient — in under 30 minutes.",
    accent: BRAND_BLUE,
    steps: [
      { icon: ShieldCheck, side: "left", color: BRAND_BLUE, tint: BLUE_TINT, eyebrow: "Foundation", title: "Save your doctor signature", body: "Open Profile → draw or upload your signature. Without it, prescriptions are blocked (legal safety, not a bug).", cta: { label: "Open Profile", to: "/profile" } },
      { icon: Share2, side: "right", color: BRAND_ORANGE, tint: ORANGE_TINT, eyebrow: "Distribute", title: "Broadcast your WhatsApp", body: "Open the Distribution Kit, copy your unique WhatsApp link + QR onto Google Business, Instagram, Facebook, your website footer & print posters. Patients land in a Pulse-Agent chat with one tap.", cta: { label: "Open Distribution Kit", to: "/distribution-kit" } },
      { icon: MessageCircle, side: "left", color: BRAND_BLUE, tint: BLUE_TINT, eyebrow: "Listen", title: "Watch the WhatsApp Mirror", body: "Every patient chat lands here in real time (polls every 5s). You see Pulse-Agent triage replies as they happen — inbound & outbound, all in one feed.", cta: { label: "Open WhatsApp Mirror", to: "/clinic", tab: "whatsapp" } },
      { icon: Mic, side: "right", color: BRAND_ORANGE, tint: ORANGE_TINT, eyebrow: "Record", title: "Record your first voice note", body: "Open Voice-to-EMR → record 30 seconds → Pulse-Agent transcribes (Whisper) + structures it into a SOAP note. Add a drug from autosuggest. Total time: 90 seconds.", cta: { label: "Open Voice-to-EMR", to: "/clinic", tab: "voice" } },
      { icon: Banknote, side: "left", color: BRAND_BLUE, tint: BLUE_TINT, eyebrow: "Earn", title: "Watch money come in", body: "Payment Ledger separates Paid vs Pending. We auto-WhatsApp + Email reminders daily at 10 AM IST. You only see clean money.", cta: { label: "Open Payment Ledger", to: "/clinic", tab: "ledger" } },
      { icon: LifeBuoy, side: "right", color: BRAND_ORANGE, tint: ORANGE_TINT, eyebrow: "Support", title: "Anything weird? One-tap support", body: "Use Help → Report Issue. Ticket flies to ram@projexino.com via Resend with full context. We reply fast.", cta: { label: "Open Help", to: "/help" } },
    ],
  },
  doctor: null,
  super_admin: {
    title: "Super-Admin command tour",
    subtitle: "The whole Pulse-Agent network at your fingertips.",
    accent: BRAND_ORANGE,
    steps: [
      { icon: Building2, side: "left", color: BRAND_BLUE, tint: BLUE_TINT, eyebrow: "Onboard", title: "Approve incoming clinic signups", body: "Signup Requests tab → review → Approve. Magic-link auto-sent on WhatsApp; password via private Resend email. Welcome guide ships with WhatsApp distribution steps built in.", cta: { label: "Signup Requests", to: "/super-admin", tab: "signups" } },
      { icon: Users, side: "right", color: BRAND_ORANGE, tint: ORANGE_TINT, eyebrow: "Manage", title: "See every tenant clinic", body: "Tenant Clinics tab → toggle access, view per-clinic revenue & patient counts.", cta: { label: "Tenant Clinics", to: "/super-admin", tab: "clinics" } },
      { icon: Megaphone, side: "left", color: BRAND_BLUE, tint: BLUE_TINT, eyebrow: "Broadcast", title: "Push announcements & posters", body: "Announcements tab → create a banner or full-screen poster. Target one clinic, several, or all.", cta: { label: "Announcements", to: "/super-admin", tab: "announcements" } },
      { icon: FileText, side: "right", color: BRAND_ORANGE, tint: ORANGE_TINT, eyebrow: "Brand", title: "Edit transactional emails", body: "Email Templates tab → preview + customize the welcome, payment-received, reminder, follow-up & prescription-ready emails. All wrapped in master layout.", cta: { label: "Email Templates", to: "/super-admin", tab: "templates" } },
      { icon: BarChart3, side: "left", color: BRAND_BLUE, tint: BLUE_TINT, eyebrow: "Monitor", title: "Cron Health on Overview", body: "Daily payment-reminder cron runs at 10 AM IST. The Cron Health widget on Overview shows next run + last 5 runs. WhatsApp Mirror shows every platform-wide message.", cta: { label: "Overview", to: "/super-admin" } },
      { icon: Activity, side: "right", color: BRAND_ORANGE, tint: ORANGE_TINT, eyebrow: "Listen", title: "Help tickets & user feedback", body: "Every issue submitted via Help Portal is queued in your inbox + the help_tickets collection. Reply directly to the user.", cta: { label: "Help tickets", to: "/help" } },
    ],
  },
  patient: {
    title: "Welcome to your Health Portal",
    subtitle: "Everything from your clinic, in one private place.",
    accent: BRAND_BLUE,
    steps: [
      { icon: Pill, side: "left", color: BRAND_ORANGE, tint: ORANGE_TINT, eyebrow: "Medicines", title: "All your prescriptions", body: "Every prescription from your doctor lives here. Download the PDF, share it with the pharmacy, or pull it up anytime.", cta: { label: "View prescriptions", to: "/patient" } },
      { icon: Mic, side: "right", color: BRAND_BLUE, tint: BLUE_TINT, eyebrow: "Voice", title: "Voice notes from your doctor", body: "If your doctor recorded a voice note for you, play it right inside the app. Replay anytime.", cta: { label: "Play voice notes", to: "/patient" } },
      { icon: Banknote, side: "left", color: BRAND_ORANGE, tint: ORANGE_TINT, eyebrow: "Bills", title: "Invoices — paid & pending", body: "See exactly what's paid and what's pending. No surprises, no hidden fees.", cta: { label: "Open invoices", to: "/patient" } },
      { icon: Phone, side: "right", color: BRAND_BLUE, tint: BLUE_TINT, eyebrow: "Chat", title: "Chat with the clinic on WhatsApp", body: "Anytime you have a question, message the clinic on WhatsApp. Pulse-Agent replies in seconds, your doctor in minutes.", cta: { label: "Open WhatsApp", to: "/patient" } },
    ],
  },
};
ROADMAPS.doctor = ROADMAPS.clinic_admin;

function TimelineCard({ step, idx, isLast, onClose }) {
  const Icon = step.icon;
  const navigate = useNavigate();
  const goto = () => {
    onClose?.();
    if (step.cta?.tab) sessionStorage.setItem("pulse_jump_tab", step.cta.tab);
    if (step.cta?.to) navigate(step.cta.to);
  };

  const isRight = step.side === "right";

  return (
    <div className="relative pop-in" style={{ animationDelay: `${0.06 * idx}s` }} data-testid={`roadmap-step-${idx}`}>
      <div className="flex items-stretch gap-4 md:gap-0">
        {/* Mobile: left node column */}
        <div className="md:hidden flex flex-col items-center w-7 flex-shrink-0">
          <div
            className="h-7 w-7 rounded-full flex items-center justify-center text-white font-bold text-xs shadow-md flex-shrink-0"
            style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, ${BRAND_ORANGE})` }}
          >
            {idx + 1}
          </div>
          {!isLast && <div className="flex-1 w-0.5 my-1" style={{ background: `linear-gradient(180deg, ${BRAND_BLUE}40, ${BRAND_ORANGE}40)` }} />}
        </div>

        {/* Desktop: 3-column row with left-card / center-node / right-card */}
        <div className="flex-1 grid md:grid-cols-[1fr_auto_1fr] md:gap-0 items-start">
          {/* Left slot */}
          <div className={`hidden md:flex md:justify-end ${isRight ? "md:invisible" : ""} md:pr-8`}>
            {!isRight && <Card step={step} icon={Icon} side="left" goto={goto} idx={idx} />}
          </div>

          {/* Center node — desktop */}
          <div className="hidden md:flex flex-col items-center w-12">
            <div
              className="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg z-10"
              style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, ${BRAND_ORANGE})` }}
            >
              {idx + 1}
            </div>
            {!isLast && <div className="flex-1 w-0.5 mt-2" style={{ background: `linear-gradient(180deg, ${BRAND_BLUE}, ${BRAND_ORANGE})`, opacity: 0.35, minHeight: "60px" }} />}
          </div>

          {/* Right slot */}
          <div className={`hidden md:block ${!isRight ? "md:invisible" : ""} md:pl-8`}>
            {isRight && <Card step={step} icon={Icon} side="right" goto={goto} idx={idx} />}
          </div>

          {/* Mobile-only card (single column) */}
          <div className="md:hidden pb-5">
            <Card step={step} icon={Icon} side="right" goto={goto} idx={idx} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({ step, icon: Icon, side, goto, idx }) {
  const arrowDir = side === "right" ? "-left-2" : "-right-2";
  return (
    <div
      className="card-3d bg-white rounded-2xl p-4 sm:p-5 border-2 relative w-full"
      style={{ borderColor: `${step.color}30` }}
    >
      <div className={`hidden md:block absolute top-7 ${arrowDir} h-3 w-3 rotate-45 bg-white border-2`}
        style={{
          borderColor: `${step.color}30`,
          borderRight: side === "right" ? "0" : "",
          borderTop: side === "right" ? "0" : "",
          borderLeft: side === "left" ? "0" : "",
          borderBottom: side === "left" ? "0" : "",
        }}
      />
      <div className="flex items-start gap-3 mb-2">
        <div className="icon-3d icon-float h-11 w-11 rounded-xl flex-shrink-0 flex items-center justify-center" style={{ background: step.tint }}>
          <Icon className="h-5 w-5" style={{ color: step.color }} />
        </div>
        <div className="min-w-0">
          <div className="tiny-label" style={{ color: step.color }}>{step.eyebrow}</div>
          <h3 className="font-heading font-bold text-base sm:text-lg text-slate-900 leading-tight mt-0.5">{step.title}</h3>
        </div>
      </div>
      <p className="text-sm text-slate-600 leading-relaxed mt-2">{step.body}</p>
      {step.cta && (
        <Button
          onClick={goto}
          size="sm"
          className="mt-3 text-white hover:opacity-90 btn-sheen group"
          style={{ background: step.color }}
          data-testid={`roadmap-step-cta-${idx}`}
        >
          {step.cta.label}
          <ArrowRight className="h-3.5 w-3.5 ml-1 transition group-hover:translate-x-1" />
        </Button>
      )}
    </div>
  );
}

export default function RoadmapDialog() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    const key = `pulse_roadmap_seen_${user.id}`;
    if (!localStorage.getItem(key)) {
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, [user]);

  const close = () => {
    if (user) localStorage.setItem(`pulse_roadmap_seen_${user.id}`, "1");
    setOpen(false);
    setManualOpen(false);
  };
  const reopen = () => setManualOpen(true);

  if (!user) return null;
  const cfg = ROADMAPS[user.role] || ROADMAPS.clinic_admin;
  const dialogOpen = open || manualOpen;

  return (
    <>
      <Dialog open={dialogOpen} onOpenChange={(v) => { if (!v) close(); }}>
        <DialogContent
          className="max-w-3xl w-[95vw] max-h-[92vh] p-0 overflow-hidden border-0"
          style={{ background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)" }}
          data-testid="roadmap-dialog"
        >
          <DialogTitle className="sr-only">{cfg.title}</DialogTitle>
          <DialogDescription className="sr-only">{cfg.subtitle}</DialogDescription>
          <button
            onClick={close}
            data-testid="roadmap-close"
            className="absolute top-4 right-4 z-20 h-9 w-9 rounded-full bg-white shadow-md hover:shadow-xl hover:scale-110 transition flex items-center justify-center"
            aria-label="Close roadmap"
          >
            <X className="h-4 w-4" style={{ color: BRAND_BLUE }} />
          </button>

          {/* Header — strictly brand gradient */}
          <div
            className="px-5 sm:px-8 pt-6 sm:pt-8 pb-5 sm:pb-6 relative overflow-hidden"
            style={{ background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, ${BRAND_BLUE} 55%, ${BRAND_ORANGE} 100%)` }}
          >
            <div className="absolute -top-10 -right-10 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute -bottom-12 -left-4 h-32 w-32 rounded-full bg-white/10 blur-xl" />
            <div className="relative flex items-center gap-3 sm:gap-4 text-white">
              <div className="icon-3d icon-float h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
                <Rocket className="h-7 w-7 sm:h-9 sm:w-9 text-white" />
              </div>
              <div className="min-w-0">
                <div className="tiny-label opacity-80">Pulse-Agent AI · Your journey</div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-heading font-extrabold leading-tight">{cfg.title}</h2>
                <p className="text-white/90 text-xs sm:text-sm md:text-base mt-1">{cfg.subtitle}</p>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="relative px-4 sm:px-6 py-6 sm:py-8 max-h-[55vh] overflow-y-auto">
            <div className="space-y-0">
              {cfg.steps.map((s, i) => (
                <TimelineCard key={i} step={s} idx={i} isLast={i === cfg.steps.length - 1} onClose={close} />
              ))}
            </div>

            {/* Final node */}
            <div className="flex flex-col items-center text-center mt-4 md:mt-6 pop-in" style={{ animationDelay: "0.5s" }}>
              <div
                className="h-12 w-12 rounded-full flex items-center justify-center text-white shadow-xl"
                style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, ${BRAND_ORANGE})` }}
              >
                <Check className="h-6 w-6" />
              </div>
              <div className="mt-2 text-sm font-bold" style={{ color: BRAND_BLUE }}>You're all set 🚀</div>
              <div className="text-xs text-slate-500">Pulse-Agent works for you 24/7.</div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-t bg-white flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-slate-500 flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" style={{ color: BRAND_ORANGE }} />
              <span className="hidden sm:inline">Reopen anytime via the rocket button.</span>
              <span className="sm:hidden">Reopen via rocket.</span>
            </p>
            <Button onClick={close} className="text-white btn-sheen hover:opacity-90" style={{ background: BRAND_ORANGE }} data-testid="roadmap-got-it">
              Got it — let's go ✨
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Floating FAB · sits above the MessageCenter launcher (which lives at bottom-5/right-5) */}
      {!dialogOpen && (
        <button
          onClick={reopen}
          data-testid="roadmap-fab"
          className="fixed bottom-24 right-5 sm:bottom-[100px] sm:right-6 z-40 h-11 w-11 sm:h-12 sm:w-12 rounded-full text-white shadow-2xl hover:scale-110 transition flex items-center justify-center icon-wiggle"
          style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, ${BRAND_ORANGE})` }}
          aria-label="Open roadmap"
          title="Open your journey roadmap"
        >
          <Rocket className="h-5 w-5" />
        </button>
      )}
    </>
  );
}
